import { useEffect } from "react";

/**
 * Lightweight SEO hook — sets document.title + updates/inserts standard, OpenGraph
 * and Twitter meta tags. No external dependency (unlike react-helmet-async).
 *
 * Usage: useSEO({ title, description, image, url, keywords })
 */
function setMeta(selector, attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (attr === "name") el.setAttribute("name", name);
    else el.setAttribute("property", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, image, url, keywords, jsonLd }) {
  useEffect(() => {
    const fullTitle = title
      ? (/swaanstay/i.test(title) ? title : `${title} — SwaanStay`)
      : "SwaanStay — India's First AI-Powered Hotel Marketplace";
    document.title = fullTitle;

    const finalUrl = url || window.location.href;
    const finalImage = image || "https://price-compete.emergent.host/og-image.png";
    const finalDesc = description || "India's first AI-powered hotel marketplace where hotels bid for your booking. Negotiate any price, save more.";

    setMeta('meta[name="description"]', "name", "description", finalDesc);
    if (keywords) setMeta('meta[name="keywords"]', "name", "keywords", keywords);

    setMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", finalDesc);
    setMeta('meta[property="og:url"]', "property", "og:url", finalUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", finalImage);

    setMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", finalDesc);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", finalImage);

    setLink("canonical", finalUrl);

    // JSON-LD schema (Product / Hotel / etc.). Inject into a stable slot so we
    // can replace it cleanly on route change.
    const slotId = "seo-jsonld-slot";
    document.getElementById(slotId)?.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = slotId;
      s.text = typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, image, url, keywords, jsonLd]);
}
